package ui.campaign.detailv2.editor.widget

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.datetime.*
import org.ssoft.common.tools.copy
import org.ssoft.common.tools.nowDate
import tools.rememberString
import tools.stringRes
import ui.widget.Space
import kotlin.time.Clock

@Composable
fun HeatMapUI(
    activeBackground: (LocalDate) -> Boolean,
    dayColor: (LocalDate) -> Color,
    showCrossOverlay: (LocalDate) -> Boolean,
    onClickAction: (LocalDate) -> (() -> Unit)?,
    modifier: Modifier = Modifier.fillMaxSize()
) {
    var months by remember {
        mutableStateOf(listOf<EntryMonth>())
    }

    LaunchedEffect(Unit) {
        months = buildMonths()
    }
    LazyVerticalGrid(
        columns = GridCells.FixedSize(384.dp),
        horizontalArrangement = Arrangement.SpaceAround,
        verticalArrangement = Arrangement.spacedBy(16.dp),
        contentPadding = PaddingValues(8.dp),
        modifier = modifier
    ) {
        items(months) {
            Item(
                month = it,
                activeBackground = activeBackground,
                dayColor = dayColor,
                onClickAction = onClickAction,
                showCrossOverlay = showCrossOverlay
            )
        }
    }
}

@Composable
private fun Item(
    month: EntryMonth,
    activeBackground: (LocalDate) -> Boolean,
    dayColor: (LocalDate) -> Color,
    onClickAction: (LocalDate) -> (() -> Unit)?,
    showCrossOverlay: (LocalDate) -> Boolean
) {
    Column(Modifier.fillMaxWidth()) {
        Text(
            text = month.date.month.stringRes().rememberString(),
            modifier = Modifier.align(Alignment.CenterHorizontally)
        )
        Space(4.dp)
        Card(
            modifier = Modifier.padding(horizontal = 4.dp),
            colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(4.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                month.weeks.forEach { week ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(2.dp)
                    ) {
                        week.forEachIndexed { wdIndex, day ->
                            if (day == null) Spacer(Modifier.weight(1F))
                            else {
                                val action = remember(day) {
                                    onClickAction(day)
                                }
                                Box(
                                    Modifier.weight(1F)
                                        .background(
                                            color = if (!activeBackground(day)) Color.Transparent
                                            else MaterialTheme.colorScheme.primaryContainer,
                                            shape = RoundedCornerShape(16)
                                        )
                                        .padding(4.dp)
                                        .clip(CircleShape)
                                        .clickable(action != null) { action?.invoke() }
                                        .background(
                                            color = dayColor(day),
                                            shape = CircleShape
                                        )
                                        .padding(2.dp)
                                ) {
                                    Text(
                                        text = day.day.toString(),
                                        color = Color.White,
                                        fontWeight = FontWeight.Bold,
                                        modifier = Modifier.align(Alignment.Center)
                                            .padding(vertical = 8.dp)
                                    )
                                    if (showCrossOverlay(day)) {
                                        Canvas(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .aspectRatio(1F)
                                        ) {
                                            drawLine(
                                                color = Color.Gray.copy(0.8F),
                                                start = androidx.compose.ui.geometry.Offset(0F, size.height),
                                                end = androidx.compose.ui.geometry.Offset(size.width, 0F),
                                                strokeWidth = 1F
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun buildMonths(): List<EntryMonth> {

    val items = mutableListOf<EntryMonth>()

    val today = Clock.nowDate()

    val initialDate = LocalDate(
        year = today.year,
        month = today.month,
        day = 1
    )

    var currentDate = LocalDate(
        year = today.year,
        month = today.month,
        day = 1
    )

    do {
        val month = currentDate.month

        currentDate = LocalDate(
            year = currentDate.year,
            month = currentDate.month,
            day = 1
        )
        val monthStart = currentDate.copy()

        currentDate = when (currentDate.dayOfWeek) {
            DayOfWeek.MONDAY -> currentDate
            DayOfWeek.TUESDAY -> currentDate.minus(1, DateTimeUnit.DAY)
            DayOfWeek.WEDNESDAY -> currentDate.minus(2, DateTimeUnit.DAY)
            DayOfWeek.THURSDAY -> currentDate.minus(3, DateTimeUnit.DAY)
            DayOfWeek.FRIDAY -> currentDate.minus(4, DateTimeUnit.DAY)
            DayOfWeek.SATURDAY -> currentDate.minus(5, DateTimeUnit.DAY)
            DayOfWeek.SUNDAY -> currentDate.minus(6, DateTimeUnit.DAY)
        }
        val weeks = mutableListOf<List<LocalDate?>>()

        do {
            weeks.add(buildList {
                repeat(7) {
                    if (currentDate.month == month) add(currentDate)
                    else add(null)
                    currentDate = currentDate.plus(1, DateTimeUnit.DAY)
                }
            })
        } while (currentDate.month == month)

        items.add(
            EntryMonth(
                monthStart,
                weeks
            )
        )

    } while (initialDate.month != currentDate.month)

    return items
}

private class EntryMonth(
    val date: LocalDate,
    val weeks: List<List<LocalDate?>>
)

